/* eslint-disable no-restricted-syntax */
import React from 'react';
import deepEqual from 'fast-deep-equal';
import getUserLocale from 'get-user-locale';
import moment from 'moment-timezone';
import 'moment/min/locales';
import { ONE_TIME_REPORTS } from '../../constants';
import { t } from 'i18next';
import { numberFormatter } from '../../helpers/formatter';
import {
  as3D,
  getReport,
  getReportsDescription
} from '../../services/reportsservice';
import {
  addDescription,
  addReports,
  decrementFetch,
  fetchingReportsData,
  filterUpdated,
  getContractData,
  incrementFetch,
  resetFetch,
  initializeReport,
  logOut,
  meteringApi,
  showError,
  toggleDescription,
  updateFilter
} from '../../reducer/actions';

const COLUMN_SEPARATION = 25; // px
const REPORT_PADDING = 3; // px
const SCROLLBAR_WIDTH = 15; // px
const MARGIN = 70; // px

const dataSnapShot = () => {
  const currentZone = moment.tz.guess();
  const userLocale = getUserLocale();
  const localeData = moment.localeData(userLocale);
  let localeFormat = localeData.longDateFormat('L');
  localeFormat = localeFormat.replace(/YYYY/, 'YY');
  const ETL_TIME = moment.utc()
    .startOf('day')
    .add(10, 'minute');
  const convertedToLocalDateTime = ETL_TIME.local();
  return {
    date: convertedToLocalDateTime.format(localeFormat),
    time: convertedToLocalDateTime.format('h:mmA'),
    zone: moment.tz(currentZone)
      .format('z'),
  };
};

export default class ReportPage extends React.Component {
  // List of us field dp oems
  us_field_dp_metering_oems = process.env.REACT_APP_USFIELD_DP_METERING_OEMS ? process.env.REACT_APP_USFIELD_DP_METERING_OEMS.split(", ") : '';
  eu_field_dp_metering_oems = process.env.REACT_APP_EU_DP_METERING_OEMS ? process.env.REACT_APP_EU_DP_METERING_OEMS.split(", ") : '';

  constructor(props) {
    super(props);
    this.width = window.innerWidth - SCROLLBAR_WIDTH - MARGIN * 2;
    this.access_token = null;
    this.dataSnapshotTime = dataSnapShot();
    this.controller = new AbortController();
    this.signal = this.controller.signal;
  }


  componentWillMount() {
    const { session, dispatch } = this.props;
    dispatch(filterUpdated(false));

    if (! session.expiration || moment()
      .isAfter(session.expiration)) {
      dispatch(logOut());
    } else {
      this.access_token = session.access_token;
    }
    this.initializePageReports();
  }

  componentWillUnmount() {
    this.controller.abort();
  }

  componentDidMount() {
    const { dispatch, presistFilters } = this.props;
    dispatch(resetFetch());
    if (((presistFilters.region_name === 'US-FIELD') && (this.us_field_dp_metering_oems.includes(presistFilters.oem_id)))
        || ((presistFilters.region_name === 'EU') && (this.eu_field_dp_metering_oems.includes(presistFilters.oem_id)))
        || ((presistFilters.region_name === 'US-VPC') && (presistFilters.oem_id === '39a9391a'))) {
      dispatch(meteringApi(true));
    }
    this.googleAnalytics();
    this.fetchFiltersData();
    if (this.isContractDataRequired) {
      this.initializePageReports();
    }
    // Fetch all reports except the Download Device Report when navigating between pages.
    if (! this.isDiviceReportPage) {
      this.fetchReports(true);
    }
    this.fetchReportsDescription();
  }

  componentDidUpdate(previousProps) {
    const {
      presistFilters, fetchData, dispatch,
    } = this.props;
    if (! deepEqual(presistFilters, previousProps.presistFilters)) {
      this.fetchFiltersData(true);
    }
    this.onFilterChange(previousProps);
    if (fetchData.fetchReports) {
      dispatch(filterUpdated(false));
      if (this.isContractDataRequired) {
        dispatch(getContractData())
          .then(() => {
            this.fetchReports(true);
          });
        this.fetchReportsDescription();
      } else {
        this.fetchReports(true);
      }
      dispatch(fetchingReportsData(false));
    }
  }

  onFilterChange(prevProps) {
    const { dispatch, presistFilters } = this.props;
    const { oem_id, duration } = presistFilters;

    if (oem_id !== prevProps.presistFilters.oem_id && this.isContractDataRequired) {
      dispatch(updateFilter({ oem_model: 'All' }));
    }

    if (duration !== prevProps.presistFilters.duration) {
      dispatch(updateFilter({ period: 1 }));
    }
  }


  fetchReports(isUpdating = false) {
    const { dispatch } = this.props;
    // Update the OEM name to the default OEM name when the OEM ID filter is set to "All" on the Billing Data or
    // Download Devices Report pages, and the user navigates away from these pages.
    if ((! this.isTransactionsPage && ! this.isDiviceReportPage) && (this.props.presistFilters.oem_id === 'All')) {
      this.props.presistFilters.oem_id = '0dfc7900';
    }
    if (this.access_token) {
      for (const report of this.reports().reports) {
        if (isUpdating) dispatch(incrementFetch());
        getReport(this.access_token, report, this.signal)
          .then((s) => {
            const payload = {
              alias: report.alias,
              report: s,
            };
            dispatch(addReports(payload));
          })
          .catch((error) => {
            dispatch(showError(error));
            throw error;
          })
          .finally(() => {
            if (isUpdating) {
              setTimeout(() => {
                dispatch(decrementFetch());
              }, 1000);
            }
          });
      }
    } else {
      dispatch(logOut());
    }
  }

  addAllOemName(reportsData) {
    let all_oem = { id: 'All', name: 'All' };
    // check if the object already exists in the array
    let exists = reportsData.some(function(element) {
      return JSON.stringify(element) === JSON.stringify(all_oem);
    });
    // if the object doesn't exist, push it into the array
    if (!exists) {
      reportsData.push(all_oem);
    }
    return reportsData
  }

  fetchFiltersData(isUpdating = false) {
    const { dispatch } = this.props;
    let reports = this.reports().filters;
    if (isUpdating) {
      reports = reports.filter(report => (! report.name.includes(ONE_TIME_REPORTS)));
    }
    if (this.access_token) {
      for (const report of this.filterByAllowedRoles(reports)) {
        getReport(this.access_token, report, this.signal)
          .then((s) => {
            // Add "All OEMs" option to the OEM filter dropdown on the Download Device Reports and Transactions pages
            if ((this.isDiviceReportPage || this.isTransactionsPage) && report.name === 'Get_List_Of_Oems') {
              s = this.addAllOemName(s);
            }
            const payload = {
              alias: report.alias,
              report: s,
            };
            dispatch(addReports(payload));
          })
          .catch((error) => {
            dispatch(showError(error));
            // throw error;
          });
      }
    } else {
      dispatch(logOut());
    }
  }

  fetchReportsDescription() {
    if (typeof this.reportsDescription === 'function') {
      const { dispatch } = this.props;

      if (this.access_token) {
        for (const report of this.reportsDescription()) {
          getReportsDescription(this.access_token, report)
            .then((s) => {
              const payload = {
                alias: report.alias,
                report: s,
              };
              dispatch(addDescription(payload));
            })
            .catch((error) => {
              dispatch(showError(error));
              // throw error;
            });
        }
      } else {
        dispatch(logOut());
      }
    }
  }

  filterByAllowedRoles(reports) {
    const { session } = this.props;
    if (reports.some(report => typeof report.roles === 'object')) {
      return reports.filter(report => (report.roles.includes(session.role)));
    }
  }

  getDatapointReport (dataPoint_type, data) {
    if(data !== undefined) {
      return (data && data.hasOwnProperty('message'))
          ? t('unableToFetchData') : (data && data[dataPoint_type]) ? numberFormatter(data[dataPoint_type]) : null;
    }
  }

  getRuleTransactionsReport(rule_evaluation, rule_execution) {
    if(rule_evaluation !== undefined && rule_execution !== undefined) {
      if (rule_evaluation && rule_execution) {
        if (rule_evaluation.hasOwnProperty('message') || rule_execution.hasOwnProperty('message')) {
          return t('unableToFetchData');
        } else {
          const ruleEvaluation = Number(rule_evaluation.rule_evaluation.toString().replace(/,/g, ''));
          const ruleExecution = Number(rule_execution.rule_execution.toString().replace(/,/g, ''));
          return (ruleEvaluation + ruleExecution) === 0 ? '0' : numberFormatter(ruleEvaluation + ruleExecution);
        }
      } else {
        return null;
      }
    }
  }

  getRuleTransactionsOverTime(ruleEvaluationOverTimeData, ruleExecutionOverTimeData) {
    if (ruleEvaluationOverTimeData && ruleExecutionOverTimeData) {
      if (ruleEvaluationOverTimeData === t('unableToFetchData') || ruleExecutionOverTimeData === t('unableToFetchData')) {
        return t('unableToFetchData');
      } else {
        return as3D('x', 'y', 'z')([
          ...ruleEvaluationOverTimeData[0].values.map(entry => ({
            z: 'rule_evaluation',
            ...entry,
          })),
          ...ruleExecutionOverTimeData[0].values.map(entry => ({
            z: 'rule_execution',
            ...entry,
          })),
        ]);
      }
    }
  }

  getTotalTransactions(datapointsTransactions, datastreamTransactions, smsTransactions, ruleEvaluationTransactions,
                       ruleExecutionTransactions) {
    if(datapointsTransactions && datastreamTransactions && smsTransactions && ruleEvaluationTransactions
        && ruleExecutionTransactions) {
      if (datapointsTransactions.hasOwnProperty('message') && datastreamTransactions.hasOwnProperty('message')
          && smsTransactions.hasOwnProperty('message') && ruleEvaluationTransactions.hasOwnProperty('message')
          && ruleExecutionTransactions.hasOwnProperty('message')) {
        return t('unableToFetchData');
      } else {
        let datapoints
          = datapointsTransactions.hasOwnProperty('message')
            ? '0'
            : datapointsTransactions.total_datapoint;
        let dataStream
          = datastreamTransactions.hasOwnProperty('message')
            ? '0'
            : datastreamTransactions.total_datastream;
        let sms
          = smsTransactions.hasOwnProperty('message')
            ? '0'
            : smsTransactions.sms;
        let ruleEvalution
          = ruleEvaluationTransactions.hasOwnProperty('message')
            ? '0'
            : ruleEvaluationTransactions.rule_evaluation;
        let ruleExecution
          = ruleExecutionTransactions.hasOwnProperty('message')
            ? '0'
            : ruleExecutionTransactions.rule_execution;
        return (Number(datapoints.replace(/,/g, ''))
          + Number(dataStream.replace(/,/g, ''))
          + Number(sms.replace(/,/g, ''))
          + Number(ruleEvalution.replace(/,/g, ''))
          + Number(ruleExecution.replace(/,/g, ''))
        );
      }
    }
  }

  initializePageReports() {
    const { dispatch } = this.props;
    this.reports()
      .reports
      .map(report => dispatch(initializeReport(report.alias)));
  }

  report(reportBuilder) {
    const { reportsData } = this.props;
    const { alias } = reportBuilder();
    const currentReport = reportsData[alias];
    return currentReport ? currentReport.data : null;
  }

  reportDescription(reportDescriptionBuilder) {
    const { reportsData } = this.props;
    const { alias } = reportDescriptionBuilder();
    const data = reportsData[alias];
    return data
      ? {
        ...data.metaData,
        isExpanded: data.descriptionExpanded,
        toggle: () => this.toggleDescription(alias),
      } : null;
  }

  toggleDescription(alias) {
    const { dispatch } = this.props;
    dispatch(toggleDescription(alias));
  }

  widthForColumns(columnCount, coveredColumns = 1) {
    const singleColumnWidth = (this.width - COLUMN_SEPARATION * (columnCount - 1))
      / columnCount - REPORT_PADDING * 2;
    return singleColumnWidth * coveredColumns + (COLUMN_SEPARATION + REPORT_PADDING * 2)
      * (coveredColumns - 1);
  }
}
